import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { removeQuote } from '../components/utils/TextUtils';
import { parseJson } from '../utils/parseJson';

/**
 * Hook pour résoudre les valeurs d'affichage avec dicos
 * Récupère automatiquement les valeurs simple et multiple du node
 * Cache court terme, invalidation fréquente
 */

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// logique d’application des transforms (fg/bg/text/icon)
const applyValueTransform = (numValue, transform) => {
  if (numValue == null || !transform || !Array.isArray(transform.ranges)) {
    return { fg: null, bg: null, text: null, icon: null };
  }
  const incl = transform.behavior?.inclusive ?? 'left'; // 'left' => [min,max)
  const clamp = !!transform.behavior?.clamp;

  let v = numValue;
  if (clamp) {
    const mins = transform.ranges.map((r) => r.min ?? -Infinity);
    const maxs = transform.ranges.map((r) => r.max ?? Infinity);
    const gmin = Math.min(...mins);
    const gmax = Math.max(...maxs);
    if (Number.isFinite(gmin) && v < gmin) v = gmin;
    if (Number.isFinite(gmax) && v > gmax) v = gmax;
  }

  const match = transform.ranges.find((r) => {
    const min = r.min ?? -Infinity;
    const max = r.max ?? Infinity;
    if (incl === 'left') return v >= min && v < max;
    return v > min && v <= max;
  });

  const picked = match ?? transform.fallback ?? {};
  return {
    fg: picked.fg ?? null,
    bg: picked.bg ?? null,
    text: picked.text ?? null,
    icon: picked.icon ?? null,
  };
};

// ======================================================
// 1) CORE PUR (aucun hook, aucun accès Redux)
//    -> tu peux l’appeler dans un .map(...) librement
// ======================================================
export function getFieldValueResolutionCore({
  fieldDef,
  // soit tu passes nodeData, soit tu donnes déjà les 3 valeurs ci-dessous :
  nodeData = null,
  rawValue: rawValueArg,
  multiRaw: multiRawArg,
  nameref: namerefArg,
  dicoNameRef = null,
  removeQuoteFn = (s) => s, // tu peux injecter removeQuote si besoin
  parseJsonFn = JSON.parse, // idem pour parseJson
}) {
  if (!fieldDef) return createEmptyValueResolution();

  const fieldName = fieldDef.fieldName;
  // extraction compatible avec les 2 modes (nodeData ou args directs)
  const rawValue =
    rawValueArg !== undefined ? rawValueArg : nodeData?.[fieldName];
  const multiRaw =
    multiRawArg !== undefined
      ? multiRawArg
      : fieldDef.isMultiple
      ? nodeData?.[`${fieldName}_multval`]
      : null;
  const nameref =
    namerefArg !== undefined ? namerefArg : nodeData?.[`nameref_${fieldName}`];

  // --- multi parsing (identique à ton code) ---
  let multiValues = [];
  if (fieldDef.isMultiple && multiRaw !== null && multiRaw !== undefined) {
    if (Array.isArray(multiRaw)) {
      multiValues = multiRaw;
    } else if (typeof multiRaw === 'string') {
      try {
        const parsed = parseJsonFn(multiRaw);
        multiValues = Array.isArray(parsed) ? parsed : [];
        console.log(
          `  📦 Parsed multi values for ${fieldName}: [${multiValues.join(
            ', '
          )}]`
        );
      } catch (e) {
        console.warn(`  ⚠️ Failed to parse multi values for ${fieldName}:`, e);
        multiValues = [];
      }
    }
  }

  // --- gestion valeur simple vide ---
  if (rawValue === null || rawValue === undefined) {
    return {
      ...createEmptyValueResolution(),
      rawValue,
      multiValues,
      hasMultiValues: multiValues.length > 0,
      isEmpty: !multiValues.length,
      isValid: true,
    };
  }

  let resolvedValue = rawValue;

  // --- nettoyage parentValue (identique) ---
  if (fieldDef.context?.wasFormulaResolved) {
    console.log(`  🔧 Nettoyage valeur formula: "${rawValue}"`);
    resolvedValue = removeQuoteFn(rawValue);
    resolvedValue = resolvedValue?.replace?.(/\\"/g, '"') ?? resolvedValue;
    console.log(`  → Valeur nettoyée: "${resolvedValue}"`);
  }

  // --- résolution dico/nameref ---
  const linkedLabel =
    fieldDef.isLinked && rawValue != null
      ? dicoNameRef?.[rawValue] ?? null
      : null;

  let displayValue = resolvedValue;
  let wasLinkedResolved = false;

  if (fieldDef.isLinked && linkedLabel != null) {
    displayValue = linkedLabel;
    wasLinkedResolved = true;
    console.log(`  📝 Résolution dico: "${displayValue}"`);
  }

  if (!displayValue && nameref) {
    displayValue = nameref;
    console.log(`  📝 Résolution nameref: "${displayValue}"`);
  }

  // --- transforms numériques ---
  let ui = { fg: null, bg: null, icon: null };
  let transformedText = null;

  if (fieldDef.isNumericLike && fieldDef.parsedValueTransform) {
    const numeric = toNumber(resolvedValue);
    if (numeric !== null) {
      const { fg, bg, text, icon } = applyValueTransform(
        numeric,
        fieldDef.parsedValueTransform
      );
      ui = { fg, bg, icon };
      if (text != null && text !== '') {
        transformedText = text;
        displayValue = text; // override texte
      }
    }
  }

  // --- format final + flags ---
  const formattedValue = fieldDef.hasUnit
    ? `${displayValue}${fieldDef.unit}`
    : displayValue;

  return {
    rawValue,
    resolvedValue,
    displayValue: displayValue === 'NULL' ? ' - ' : displayValue,
    formattedValue,

    multiValues,
    hasMultiValues: multiValues.length > 0,

    isUrl:
      displayValue?.startsWith?.('http') || displayValue?.startsWith?.('www'),
    isEmpty: !displayValue && !multiValues.length,
    isResolved: displayValue !== rawValue,

    ui, // { fg, bg, icon }
    transformedText,

    hasNameref: Boolean(nameref),
    wasLinkedResolved,

    isValid: true,
    error: null,
  };
}

export const useFieldValueResolver = ({ fieldDef, node = null }) => {
  const dicoNameRef = useSelector((s) => s.objects.dicoNameRef);

  // On n’extrait que ce qui est nécessaire (stabilise les deps)
  const fieldName = fieldDef?.fieldName;
  const rawValue = fieldName ? node?.data?.[fieldName] : undefined;
  const multiRaw =
    fieldName && fieldDef?.isMultiple
      ? node?.data?.[`${fieldName}_multval`]
      : undefined;
  const nameref = fieldName ? node?.data?.[`nameref_${fieldName}`] : undefined;

  return useMemo(() => {
    if (!fieldDef || !node?.data) return createEmptyValueResolution();
    return getFieldValueResolutionCore({
      fieldDef,
      rawValue,
      multiRaw,
      nameref,
      dicoNameRef,
      removeQuoteFn: removeQuote,
      parseJsonFn: parseJson,
    });
  }, [
    // inputs “data”
    rawValue,
    multiRaw,
    nameref,
    dicoNameRef,
    // inputs “schema”
    fieldDef,
    fieldDef?.parsedValueTransform,
    fieldDef?.isNumericLike,
    fieldDef?.unit,
    fieldDef?.hasUnit,
    fieldDef?.isLinked,
    fieldDef?.context?.wasFormulaResolved,
  ]);
};

export const useFieldValueResolverOLD = ({ fieldDef, node = null }) => {
  const dicoNameRef = useSelector((state) => state.objects.dicoNameRef);

  // ===================================================================
  // EXTRACTION SPÉCIFIQUE DES VALEURS NÉCESSAIRES
  // (évite les problèmes de memo avec node.data complet)
  // ===================================================================
  const fieldName = fieldDef?.fieldName;
  const rawValue = node?.data?.[fieldName];
  const multiRaw = fieldDef?.isMultiple
    ? node?.data?.[`${fieldName}_multval`]
    : null;
  const nameref = node?.data?.[`nameref_${fieldName}`];

  const linkedLabel = useSelector((state) =>
    rawValue != null ? state.objects.dicoNameRef?.[rawValue] ?? null : null
  );

  return useMemo(() => {
    if (!fieldDef?.isValid || !node?.data) {
      return createEmptyValueResolution();
    }

    // ===================================================================
    // PARSING DES VALEURS MULTIPLES
    // ===================================================================
    let multiValues = [];
    if (fieldDef.isMultiple && multiRaw !== null && multiRaw !== undefined) {
      if (Array.isArray(multiRaw)) {
        multiValues = multiRaw;
      } else if (typeof multiRaw === 'string') {
        try {
          const parsed = parseJson(multiRaw);
          multiValues = Array.isArray(parsed) ? parsed : [];
          console.log(
            `  📦 Parsed multi values for ${fieldName}: [${multiValues.join(
              ', '
            )}]`
          );
        } catch (e) {
          console.warn(
            `  ⚠️ Failed to parse multi values for ${fieldName}:`,
            e
          );
          multiValues = [];
        }
      }
    }

    // ===================================================================
    // GESTION VALEUR SIMPLE VIDE
    // ===================================================================
    if (rawValue === null || rawValue === undefined) {
      return {
        ...createEmptyValueResolution(),
        rawValue,
        multiValues,
        hasMultiValues: multiValues.length > 0,
        isEmpty: !multiValues.length,
        isValid: true,
      };
    }

    let resolvedValue = rawValue;

    // ===================================================================
    // GESTION SPÉCIALE : VALEUR DES FORMULAS PARENTVALUE
    // ===================================================================
    if (fieldDef.context.wasFormulaResolved) {
      console.log(`  🔧 Nettoyage valeur formula: "${rawValue}"`);
      // Nettoyer la valeur comme dans le code original
      resolvedValue = removeQuote(rawValue);
      resolvedValue = resolvedValue?.replace(/\\"/g, '"') || resolvedValue;
      console.log(`  → Valeur nettoyée: "${resolvedValue}"`);
    }

    // ===================================================================
    // RÉSOLUTION AVEC DICOS (utilise le champ résolu)
    // ===================================================================
    let displayValue = resolvedValue;
    let wasLinkedResolved = false;

    // Résolution via dicoNameRef (pour champs liés) — version synchronisée
    if (fieldDef.isLinked && linkedLabel != null) {
      displayValue = linkedLabel;
      console.log(`  📝 Résolution dico: "${displayValue}"`);
    }

    // Résolution via nameref (cas spécial DataCell)
    if (!displayValue && nameref) {
      displayValue = nameref;
      console.log(`  📝 Résolution nameref: "${displayValue}"`);
    }

    // ===================================================================
    // TRANSFORME l’AFFICHAGE pour les types numériques (valueTransform)
    // ===================================================================
    let ui = { fg: null, bg: null, icon: null };
    let transformedText = null;

    if (fieldDef.isNumericLike && fieldDef.parsedValueTransform) {
      const numeric = toNumber(resolvedValue);
      if (numeric !== null) {
        const { fg, bg, text, icon } = applyValueTransform(
          numeric,
          fieldDef.parsedValueTransform
        );
        ui = { fg, bg, icon };
        // si un texte est fourni par la règle → il “prend la main” sur l'affichage brut
        if (text != null && text !== '') {
          transformedText = text;
          displayValue = text; // on remplace la valeur affichée
        }
      }
    }

    wasLinkedResolved = displayValue && fieldDef.isLinked;

    // Formatage final avec unité (du champ résolu)
    const formattedValue = fieldDef.hasUnit
      ? `${displayValue}${fieldDef.unit}`
      : displayValue;

    return {
      // === VALEURS PRINCIPALES ===
      rawValue,
      resolvedValue,
      displayValue: displayValue === 'NULL' ? ' - ' : displayValue, // XXX voir si c'est à ce niveau qu'il faut faire ce check
      formattedValue,

      // === VALEURS MULTIPLES ===
      multiValues,
      hasMultiValues: multiValues.length > 0,

      // === FLAGS DE CONTENU ===
      isUrl:
        displayValue?.startsWith?.('http') || displayValue?.startsWith?.('www'),
      isEmpty: !displayValue && !multiValues.length,
      isResolved: displayValue !== rawValue,

      // === UI dérivée des transforms ===
      ui, // { fg, bg, icon }
      transformedText, // null ou string si la règle a fourni un texte

      // === MÉTADONNÉES CONTEXTUELLES ===
      hasNameref: Boolean(nameref),
      wasLinkedResolved,

      // === MÉTA ===
      isValid: true,
      error: null,
    };
  }, [
    fieldDef,
    rawValue,
    multiRaw,
    nameref,
    linkedLabel,
    fieldDef?.parsedValueTransform,
    fieldDef?.isNumericLike,
    fieldDef?.unit,
    fieldDef?.hasUnit,
    fieldDef?.isLinked,
    fieldDef?.context?.wasFormulaResolved,
  ]); // Dépendances spécifiques pour optimiser le memo
};

/**
 * Crée une résolution de valeur vide (fallback pour useFieldValueResolver)
 */
export const createEmptyValueResolution = () => ({
  // === VALEURS PRINCIPALES ===
  rawValue: null,
  resolvedValue: null,
  displayValue: '',
  formattedValue: '',

  // === VALEURS MULTIPLES ===
  multiValues: [],
  hasMultiValues: false,

  // === FLAGS DE CONTENU ===
  isUrl: false,
  isEmpty: true,
  isResolved: false,

  // === MÉTADONNÉES CONTEXTUELLES ===
  hasNameref: false,
  wasLinkedResolved: false,

  ui: { fg: null, bg: null, icon: null },
  transformedText: null,

  // === MÉTA ===
  isValid: false,
  error: 'Missing fieldDef or node',
});
